import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, message, Table, theme as antdTheme, Progress, DatePicker, Input, Space, Button } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  ApiOutlined,
  TrophyOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { logApi } from '../../services/api';
import { LogStats, SystemStats } from '../../types';
import dayjs from 'dayjs';
// import { Pie } from '@ant-design/plots'; // 移除有问题的饼图组件
import { useTheme } from '../../context/ThemeContext';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// 自定义饼图组件
const CustomPieChart: React.FC<{
  data: Array<{ label: string; value: number; color: string }>;
  width?: number;
  height?: number;
}> = ({ data, width = 400, height = 400 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme: appTheme } = useTheme();
  const { token } = antdTheme.useToken();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data.length) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置高DPI支持
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // 清除画布
    ctx.clearRect(0, 0, width, height);

    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 3;

    // 计算总值
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return;

    let currentAngle = -Math.PI / 2; // 从顶部开始

    // 绘制饼图扇形
    data.forEach((item) => {
      const sliceAngle = (item.value / total) * 2 * Math.PI;

      // 绘制扇形
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = item.color;
      ctx.fill();

      // 绘制边框
      ctx.strokeStyle = appTheme === 'dark' ? '#424242' : '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // 绘制标签
      const labelAngle = currentAngle + sliceAngle / 2;
      const labelX = centerX + Math.cos(labelAngle) * (radius * 0.7);
      const labelY = centerY + Math.sin(labelAngle) * (radius * 0.7);

      const percentage = ((item.value / total) * 100).toFixed(1);

      // 只在扇形足够大时显示标签
      if (sliceAngle > 0.2) {
        ctx.fillStyle = appTheme === 'dark' ? '#ffffff' : '#000000';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${percentage}%`, labelX, labelY);
      }

      currentAngle += sliceAngle;
    });
  }, [data, width, height, appTheme, token]);

  return <canvas ref={canvasRef} style={{ display: 'block', margin: '0 auto' }} />;
};

// 图例组件
const Legend: React.FC<{
  data: Array<{ label: string; value: number; color: string }>;
}> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div style={{ marginTop: 16 }}>
      {data.map((item) => {
        const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0';
        return (
          <div key={item.label} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 8,
            fontSize: '14px'
          }}>
            <div style={{
              width: 16,
              height: 16,
              backgroundColor: item.color,
              marginRight: 8,
              borderRadius: 2
            }} />
            <span style={{ flex: 1 }}>{item.label}</span>
            <span style={{ marginLeft: 8, fontWeight: 500 }}>
              {item.value} ({percentage}%)
            </span>
          </div>
        );
      })}
    </div>
  );
};

const StatsPage: React.FC = () => {
  const [logStats, setLogStats] = useState<LogStats | null>(null);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const { theme: appTheme } = useTheme();
  const { token } = antdTheme.useToken();

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const logParams = {
        start_time: dateRange[0]?.startOf('day').toISOString(),
        end_time: dateRange[1]?.endOf('day').toISOString(),
        username: username || undefined
      };

      const [logResponse, systemResponse] = await Promise.all([
        logApi.getLogStats(logParams),
        logApi.getSystemStats(),
      ]);
      setLogStats(logResponse.data);
      setSystemStats(systemResponse.data);
    } catch (error) {
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // 调试：输出后端actions_stats内容
  console.log('原始 actions_stats:', logStats?.actions_stats);
  console.log('logStats 完整数据:', logStats);

  // 安全的数据处理函数
  const safeNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = typeof value === 'number' ? value : Number(value);
    const result = isNaN(num) || !isFinite(num) ? 0 : Math.max(0, num);
    console.log(`safeNumber: ${value} -> ${result}`);
    return result;
  };

  // 操作统计表格数据
  const actionStatsData = logStats?.actions_stats
    ? Object.entries(logStats.actions_stats)
      .filter(([action, count]) => {
        const safeCount = safeNumber(count);
        const isValid = action && action.trim() && safeCount > 0;
        console.log(`过滤操作: ${action} = ${count}, safeCount = ${safeCount}, isValid = ${isValid}`);
        return isValid;
      })
      .map(([action, count], index) => {
        const safeCount = safeNumber(count);
        const safeTotalLogs = safeNumber(logStats.total_logs);
        const percentage = safeTotalLogs > 0 ? (safeCount / safeTotalLogs) * 100 : 0;

        return {
          key: index,
          action,
          count: safeCount,
          percentage: isFinite(percentage) ? percentage.toFixed(2) : '0.00',
        };
      })
      .sort((a, b) => b.count - a.count)
    : [];

  const actionStatsColumns = [
    {
      title: '排名',
      key: 'rank',
      render: (_: any, __: any, index: number) => index + 1,
      width: 80,
    },
    {
      title: '操作类型',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: '次数',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: string) => `${percentage}%`,
    },
  ];

  // 操作类型中文映射（与日志管理页面保持一致）
  const getActionText = (action: string) => {
    if (!action || !action.trim()) return '未知';
    const textMap: Record<string, string> = {
      'login': '登录',
      'logout': '登出',
      'create_user': '创建用户',
      'update_user': '更新用户',
      'delete_user': '删除用户',
      'regen_api_key': '重新生成API密钥',
      'add_balance': '充值',
      'deduct_balance': '扣费',
      'api_call': 'API调用',
      'balance_change': '余额变更',
      'user_update': '用户信息更新',
    };
    // 未知类型直接返回原始action
    return textMap[action.trim()] || action.trim();
  };

  // 生成颜色数组
  const getColor = (index: number) => {
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
      '#fa8c16', '#13c2c2', '#eb2f96', '#2f54eb', '#fa541c'
    ];
    return colors[index % colors.length];
  };

  // 处理饼图数据
  const pieChartData = logStats?.actions_stats && Object.keys(logStats.actions_stats).length > 0
    ? Object.entries(logStats.actions_stats)
      .filter(([action, count]) => {
        const safeCount = safeNumber(count);
        return action && action.trim() && safeCount > 0;
      })
      .map(([action, count], index) => ({
        label: getActionText(action),
        value: safeNumber(count),
        color: getColor(index),
      }))
      .sort((a, b) => b.value - a.value)
    : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>日志统计信息</Title>
        <Space>
          <Input
            placeholder="用户名搜索"
            value={username}
            onChange={e => setUsername(e.target.value)}
            onPressEnter={fetchStats}
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            allowClear
            style={{ width: 200 }}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
            allowClear={false}
          />
          <Button type="primary" onClick={fetchStats} icon={<SearchOutlined />}>
            查询
          </Button>
        </Space>
      </div>

      {/* 日志统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总日志数"
              value={logStats?.total_logs || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="今日日志"
              value={logStats?.today_logs || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作统计排行 */}
      <Card title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TrophyOutlined style={{ marginRight: 8, color: '#faad14' }} />
          操作类型排行榜
        </div>
      }>
        {/* 自定义饼图展示 */}
        {pieChartData.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            <Title level={4} style={{ marginBottom: 16, textAlign: 'center' }}>操作类型分布</Title>
            <Row gutter={24}>
              <Col span={12}>
                <CustomPieChart data={pieChartData} width={300} height={300} />
              </Col>
              <Col span={12}>
                <Legend data={pieChartData} />
              </Col>
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
            暂无操作统计数据
          </div>
        )}

        <Table
          dataSource={actionStatsData}
          columns={actionStatsColumns}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default StatsPage; 